import axios, { AxiosHeaders } from "axios";

import { clearStoredToken, getStoredToken } from "~/services/authStorage";
import { emitAuthUnauthorized } from "~/services/authEvents";

// An unset VITE_API_URL is injected as "", which would silently make every
// request relative to the SPA origin — treat empty as missing.
const baseURL = import.meta.env.VITE_API_URL || "http://localhost:3000";
const TOKEN_EXPIRED_MESSAGE = "invalid or expired token";
let didHandleUnauthorized = false;

export function resetUnauthorizedHandling() {
  didHandleUnauthorized = false;
}

function extractErrorMessage(error: unknown): string {
  if (!axios.isAxiosError(error)) return "";
  const data = error.response?.data as
    | { message?: unknown; error?: { message?: unknown } }
    | undefined;
  if (typeof data?.message === "string") return data.message;
  if (typeof data?.error?.message === "string") return data.error.message;
  return "";
}

export const apiClient = axios.create({
  baseURL,
});

apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    const headers = AxiosHeaders.from(config.headers ?? {});
    headers.set("Authorization", `Bearer ${token}`);
    config.headers = headers;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401) {
      if (didHandleUnauthorized) {
        return Promise.reject(error);
      }
      didHandleUnauthorized = true;
      clearStoredToken();
      const message = extractErrorMessage(error).trim();
      const isTokenExpired = message.toLowerCase().includes(TOKEN_EXPIRED_MESSAGE);
      emitAuthUnauthorized({
        reason: isTokenExpired ? "token-expired" : "unauthorized",
        message: isTokenExpired
          ? "Your session expired. Please sign in again."
          : undefined,
      });
    }
    return Promise.reject(error);
  }
);
